import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SESSION_WARNING_SOURCE } from '../../services/session-warning-source';

@Component({
  selector: 'app-session-warning-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
  ],
  template: `
    <p-dialog
      header="Session Expiring Soon"
      [visible]="showSessionWarning()"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '450px' }"
      [position]="'top'">

      <div class="session-warning-content">
        <div class="warning-icon">
          <i class="pi pi-exclamation-triangle"></i>
        </div>

        <div class="warning-text">
          <p class="warning-message">
            Your session will expire in <strong>{{ timeRemainingDisplay() }}</strong>.
          </p>
          <p class="warning-details">
            To avoid losing your work, please extend your session or save your progress.
          </p>
        </div>

        <div class="progress-section">
          <div class="progress-bar-container">
            <div
              class="progress-bar-fill"
              [style.width.%]="progressPercent()">
            </div>
          </div>
          <small class="progress-text">{{ timeRemainingDisplay() }} remaining</small>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="dialog-footer">
          <button
            pButton
            label="Extend Session"
            icon="pi pi-refresh"
            (click)="extendSession()"
            [loading]="isExtending()"
            class="p-button-primary">
          </button>

          <button
            pButton
            label="Logout Now"
            icon="pi pi-sign-out"
            (click)="logout()"
            class="p-button-outlined p-button-secondary">
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .session-warning-content {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .warning-icon {
      display: flex;
      justify-content: center;
      color: #ff9800;
      font-size: 3rem;
      margin-bottom: 0.5rem;
    }

    .warning-icon i {
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    .warning-text {
      text-align: center;
    }

    .warning-message {
      font-size: 1.1rem;
      font-weight: 600;
      color: #333;
      margin: 0 0 0.5rem 0;
    }

    .warning-details {
      color: #666;
      margin: 0;
      line-height: 1.5;
    }

    .progress-section {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .progress-text {
      text-align: center;
      color: #666;
      font-size: 0.875rem;
    }

    .progress-bar-container {
      width: 100%;
      height: 6px;
      background-color: #e0e0e0;
      border-radius: 3px;
      overflow: hidden;
    }

    .progress-bar-fill {
      height: 100%;
      background-color: #ff9800;
      border-radius: 3px;
      transition: width 1s linear;
    }

    .dialog-footer {
      display: flex;
      gap: 1rem;
      justify-content: center;
    }

    /* Responsive design */
    @media (max-width: 480px) {
      ::ng-deep .p-dialog {
        width: 90vw !important;
        max-width: 400px;
      }

      .warning-message {
        font-size: 1rem;
      }

      .dialog-footer {
        flex-direction: column;
      }

      .dialog-footer button {
        width: 100%;
      }
    }
  `]
})
export class SessionWarningDialogComponent {
  private sessionManager = inject(SESSION_WARNING_SOURCE);

  // Component state — bind to store's isRefreshing so spinner is accurate
  isExtending = this.sessionManager.isRefreshing;

  // Use store computed properties directly - bind dialog visibility to store signal.
  // Returning to a hidden tab must not call extendSession(): that closed the
  // warning before the user could see it (MYB-5195). SessionActivityService
  // shows the warning on visibility, and SessionStore refreshes the JWT.
  public showSessionWarning = this.sessionManager.showSessionWarning;
  public timeRemainingDisplay = this.sessionManager.timeRemainingDisplay;
  public progressPercent = this.sessionManager.progressPercent;

  extendSession(): void {
    // Must call extendSession(), not just refreshToken(), so that
    // SessionActivityService.resetActivity() is called and the inactivity
    // logout timer is rescheduled from now. Calling refreshToken() alone
    // refreshes the JWT but leaves the inactivity countdown running, causing
    // the user to be logged out within the remaining warning window.
    this.sessionManager.extendSession();
  }

  logout(): void {
    this.sessionManager.logout();
  }
}